import type { PlaybackAlert, PlaybackPoint } from "./models";

/** A passed alert counts as "just fired" for this long (real time, so the
 *  same few seconds on screen at any playback speed). */
export const JUST_FIRED_MS = 5_000;

/** Every alert of the playback, by when it fired (the points carry them).
 *  A playback can have 50,000 points but few alerts: worked out once. */
export function playbackAlertsOf(points: readonly PlaybackPoint[]): PlaybackAlert[] {
  const alerts = points.flatMap((point) => point.alerts);
  return alerts.sort((a, b) => a.occurredMs - b.occurredMs);
}

/** How many alerts had fired by `positionMs` (binary search over the sorted
 *  alerts: cheap enough for every clock tick). */
export function passedAlertCount(alerts: readonly PlaybackAlert[], positionMs: number): number {
  let low = 0;
  let high = alerts.length;
  while (low < high) {
    const middle = (low + high) >> 1;
    if (alerts[middle]!.occurredMs <= positionMs) low = middle + 1;
    else high = middle;
  }
  return low;
}

/** The alerts that fired in the last few seconds of playback, newest first:
 *  a span of history that grows with the speed. */
export function justFiredAlerts(
  alerts: readonly PlaybackAlert[],
  positionMs: number,
  speed: number,
): PlaybackAlert[] {
  const passed = passedAlertCount(alerts, positionMs);
  const sinceMs = positionMs - JUST_FIRED_MS * speed;
  const fired: PlaybackAlert[] = [];
  for (let index = passed - 1; index >= 0; index -= 1) {
    const alert = alerts[index]!;
    if (alert.occurredMs <= sinceMs) break;
    fired.push(alert);
  }
  return fired;
}

/** How far one tick of playing moves the playhead at most, per 1× speed (a
 *  tick is 100 ms; a background tab ticks about once a second). */
export const MAX_PLAYING_STEP_MS = 1_500;

/**
 * The newest alert a step of playing ran the playhead over (from `fromMs`,
 * excluded, to `toMs`), or `null` -- also for a step back, or one longer
 * than playing makes at `speed` (a jump to elsewhere).
 */
export function alertRunOver(
  alerts: readonly PlaybackAlert[],
  fromMs: number,
  toMs: number,
  speed: number,
): PlaybackAlert | null {
  const step = toMs - fromMs;
  if (step <= 0 || step > MAX_PLAYING_STEP_MS * speed) return null;
  const passed = passedAlertCount(alerts, toMs);
  return passed > passedAlertCount(alerts, fromMs) ? alerts[passed - 1]! : null;
}

/** The alerts inside what plays (a segment, or the whole range). */
export function alertsWithin(
  alerts: readonly PlaybackAlert[],
  startMs: number,
  endMs: number,
): PlaybackAlert[] {
  return alerts.filter((alert) => alert.occurredMs >= startMs && alert.occurredMs <= endMs);
}
